import { captureException } from '@sentry/react';
import { AxiosResponse } from 'axios';
import { StateCreator } from 'zustand';

import { authorizedAxiosInstance } from '@/clients';
import {
    appendEntity,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
} from '@/stores/helpers/collections';
import { ConnectedAccount } from '@/types';

import { ConnectedAccountActions, ConnectedAccountState } from '../types';

export const createConnectedAccountActions: StateCreator<
    ConnectedAccountState & ConnectedAccountActions,
    [['zustand/immer', never]],
    [],
    ConnectedAccountActions
> = (set, get, store) => ({
    // Core
    fetchConnectedAccounts: async (params) => {
        set({ loading: true });

        try {
            const res: AxiosResponse = await authorizedAxiosInstance.get('/api/connected-accounts', { params });

            if (res.status === 200) {
                set({ connectedAccounts: res.data, loading: false, error: undefined });
            }
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load connectedAccounts' });
        }
    },
    fetchConnectedAccountById: async (id) => {
        set((state) => {
            state.loadingConnectedAccount = true;
            state.error = undefined;
        });

        try {
            const res = await authorizedAxiosInstance.get(`/api/connected-accounts/${id}`);

            set((state) => {
                state.selectedConnectedAccount = res.data;
                state.loadingConnectedAccount = false;
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch connectedAccount detail';
                state.loadingConnectedAccount = false;
            });
        }
    },
    clearSelectedConnectedAccount: () => {
        set((state) => {
            state.selectedConnectedAccount = null;
        });
    },
    createConnectedAccount: async (input) => {
        const tempId = `temp-${Date.now()}`;
        const optimistic = { id: tempId, ...input } as ConnectedAccount;

        set({ connectedAccounts: appendEntity(get().connectedAccounts, optimistic) });

        try {
            const res: AxiosResponse = await authorizedAxiosInstance.post('/api/connected-accounts', input);

            if (res.status === 200) {
                set({ connectedAccounts: replaceEntityById(get().connectedAccounts, tempId, res.data) });

                return res.data;
            }
        } catch (error) {
            captureException(error);

            set({
                connectedAccounts: removeEntityById(get().connectedAccounts, tempId),
                error: 'Failed to create connectedAccount',
            });
        }
    },
    updateConnectedAccount: async (id, updates) => {
        const prev = get().connectedAccounts.find((f) => f.id === id);
        if (!prev) {
            return;
        }

        set({
            connectedAccounts: mergeEntityById(get().connectedAccounts, id, updates),
            selectedConnectedAccount:
                get().selectedConnectedAccount?.id === id
                    ? mergeEntity(get().selectedConnectedAccount!, updates)
                    : get().selectedConnectedAccount,
        });

        try {
            const res = await authorizedAxiosInstance.put(`/api/connected-accounts/${id}`, updates);

            if (res.status === 200) {
                set({
                    connectedAccounts: mergeEntityById(get().connectedAccounts, id, res.data),
                    selectedConnectedAccount:
                        get().selectedConnectedAccount?.id === id
                            ? mergeEntity(get().selectedConnectedAccount!, res.data)
                            : get().selectedConnectedAccount,
                });
            }
        } catch (error) {
            captureException(error);

            set({
                connectedAccounts: replaceEntityById(get().connectedAccounts, id, prev),
                selectedConnectedAccount:
                    get().selectedConnectedAccount?.id === id ? prev : get().selectedConnectedAccount,
                error: 'Failed to update connectedAccount',
            });
        }
    },
    deleteConnectedAccount: async (id) => {
        const prev = get().connectedAccounts;

        set({
            connectedAccounts: removeEntityById(get().connectedAccounts, id),
            selectedConnectedAccount: get().selectedConnectedAccount?.id === id ? null : get().selectedConnectedAccount,
        });

        try {
            await authorizedAxiosInstance.delete(`/api/connected-accounts/${id}`);
        } catch (error) {
            captureException(error);

            set({ connectedAccounts: prev, error: 'Failed to delete connectedAccount' });
        }
    },
});
